import {
  CreateAppointmentResponse,
  MultiDayCreateResponse,
  TransactionResponse,
} from "../types/transactions";

/**
 * Multi-service booking API contract (backend day-partitioning):
 *
 * The create endpoint (POST /appointments) accepts a FLAT `booked_services`
 * list and groups it by salon-local day — services on the same day become ONE
 * transaction (one payment); services on different days become SEPARATE
 * transactions. The response is additive:
 *   - single salon-local day  → a bare TransactionResponse (unchanged shape)
 *   - multiple days           → { transactions: TransactionResponse[] }
 *
 * These helpers normalize that union so callers can treat every booking as a
 * list of created transactions without branching on the shape.
 */

/** True when the create response is the multi-day `{ transactions: [...] }` shape. */
export function isMultiDayCreateResponse(
  response: CreateAppointmentResponse | null | undefined,
): response is MultiDayCreateResponse {
  return (
    !!response &&
    Array.isArray((response as MultiDayCreateResponse).transactions)
  );
}

/**
 * Normalize a create-appointment response to the list of created transactions.
 * Single-day → `[response]`; multi-day → `response.transactions`. Returns `[]`
 * for a null/empty response so callers never crash on an unexpected shape.
 */
export function getCreatedTransactions(
  response: CreateAppointmentResponse | null | undefined,
): TransactionResponse[] {
  if (!response) return [];
  if (isMultiDayCreateResponse(response)) return response.transactions;
  return [response as TransactionResponse];
}
